import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { reject } from 'promise';
import React from 'react';
import Dropzone, { DropzoneState } from 'react-dropzone';

import * as FileUtility from '../../../../util/file-repos-utility';

const styles = require('./dropzone.css');

interface BasicDropzoneProps {
  debug?: boolean;
  onDrop?(files: Array<File>): void;
  ref?: BasicDropzone;
  files?(af: Array<File>): void;
}

interface DropzoneProps extends BasicDropzoneProps {
  accept?: string;
  minSize?: number;
  maxSize?: number;
  multiple?: boolean;
  contents?(c: Array<FileContent>): void;
}

export interface FileContent {
  name: string;
  content: string | ArrayBuffer;
  contentType?: string;
  /// This has to be a string to pass directly to an <img> DOM object,
  /// so when reading from FileReader, use 'as string' cast
  dataUri?: string;
}

const MAX_CSV_SIZE = 1048576;
const MAX_IMG_SIZE = 8388608;
const CSV_ACCEPT = 'text/plain, text/csv, application/vnd.ms-excel';
/// Maybe add image/gif to this list
const IMG_ACCEPT = 'image/png, image/jpeg, image/x-icon, application/octet-stream';
const FILES_TOO_MANY = 'Too many files.';
const FILE_TOO_LARGE = 'File is too large.';
const FILE_WRONG_TYPE = 'File type not accepted.';
const FILE_ERROR = 'Error uploading file.';

@observer
export class BasicDropzone extends React.Component<BasicDropzoneProps> {
  @observable fileDrop: Array<File> = [];
  @action onDrop = (files: Array<File>) => {
    /// --- [ FIXME ] ---
    // tslint:disable: no-console
    // tslint:disable: no-unused-expression
    this.props.debug && console.log('files:', files);
    this.fileDrop = files;
    this.props.debug && console.log('this.files:', this.fileDrop);
    this.props.files && this.props.files(this.files);
  };
  @computed get files() {
    this.props.debug && console.log('get files:', this.fileDrop);
    return this.fileDrop;
  }

  renderFileNames = () => {
    return (
      <aside>
        <h4>Files</h4>
        <ul>
          {this.files.map((file: File) => (
            <li
              // className="list-group-item list-group-item-success"
              key={file.name}
            >
              {file.name}
            </li>
          ))}
        </ul>
      </aside>
    );
  };

  /// Basic dropzone - no accept, minSize, maxSize or isDragReject props
  render() {
    const files = this.props.debug ? this.renderFileNames() : null;
    return (
      <Dropzone onDrop={this.onDrop}>
        {(state: DropzoneState) => (
          <section className={styles.container}>
            <div {...state.getRootProps({ className: styles.dropzone })}>
              <input {...state.getInputProps()} />
              <p>Drag and drop here, or click to select files</p>
            </div>
            {files}
          </section>
        )}
      </Dropzone>
    );
  }
}

@observer
export class CSVDropzone extends React.Component<DropzoneProps> {
  @observable acceptedFiles: Array<File> = [];
  @observable content: Array<FileContent> = [];
  @action onDrop = (files: Array<File>) => {
    this.props.debug && console.log('files:', files);
    this.acceptedFiles = files;
    this.props.debug && console.log('this.files:', this.acceptedFiles);
    this.acceptedFiles.forEach(file => this.textFileHandler(file));
    this.props.files && this.props.files(this.files);
    this.props.contents && this.props.contents(this.content);
  };

  @computed get files() {
    this.props.debug && console.log('get files:', this.acceptedFiles);
    return this.acceptedFiles;
  }

  textFileHandler = async (file: File) => {
    const textStr = await FileUtility.readFileAsText(file);
    runInAction(() => {
      // const textStr = reader.result;
      this.props.debug && console.log('textFileHandler:', textStr);
      textStr && this.content.push({ name: file.name, content: textStr });
    });

    // const reader = new FileReader();

    // reader.readAsText(file);

    // /// TODO HAndle these errors with the error message handler
    // reader.onabort = () => console.log("file reading was aborted");
    // reader.onerror = () => console.log("file reading has failed");
    // ///
    // /// --- [ Possible TODO Move some/all of this to reader.onload ] ---
    // ///
    // reader.onloadend = () => {
    //   runInAction(() => {
    //     const textStr = reader.result;
    //     this.props.debug && console.log("textFileHandler:", textStr);
    //     textStr && this.content.push({ name: file.name, content: textStr });
    //   });
    // };
  };

  renderFileNames = () => {
    return (
      <aside>
        <h4>Files</h4>
        <ul>
          {this.acceptedFiles.length > 0 &&
            this.files.map((file: File) => (
              <li
                // className="list-group-item list-group-item-success"
                key={file.name}
              >
                {file.name}
                <br />
              </li>
            ))}
        </ul>
      </aside>
    );
  };

  render() {
    const files = this.props.debug ? this.renderFileNames() : null;
    const checkDropOk = (state: DropzoneState) => {
      if (state.rejectedFiles.length > 0) {
        if (state.rejectedFiles.length > 1) {
          return FILES_TOO_MANY;
        } else {
          if (state.rejectedFiles[0].size > MAX_CSV_SIZE) {
            return `${state.rejectedFiles[0].name}: ${FILE_TOO_LARGE}`;
          }
          /// --- [ TODO Check this works in other browsers ] ---
          if (!state.rejectedFiles[0].type.match(/^text/)) {
            return `${state.rejectedFiles[0].name}: ${FILE_WRONG_TYPE}`;
          } else {
            return `${state.rejectedFiles[0].name}: ${FILE_ERROR}`;
          }
        }
      } else {
        return null;
      }
    };
    /// Believe there is a bug in the implementation of react-dropzone, in that
    /// isDragReject always returns false. So it's effectively useless
    ///
    /// --- [ TODO Pass props through if set in caller env ] ---
    return (
      <Dropzone onDrop={this.onDrop} accept={CSV_ACCEPT} minSize={0} maxSize={MAX_CSV_SIZE} multiple={false}>
        {(state: DropzoneState) => (
          <section className={styles.container}>
            <div {...state.getRootProps({ className: styles.dropzone })}>
              <input {...state.getInputProps()} />
              {!state.isDragActive && !state.isDragReject && 'Click here or drop a file to upload'}
              {/* {state.isDragActive && !state.isDragReject && "Accepting files..."} */}
              {state.isDragActive && 'Accepting files...'}
              {!state.isDragActive && checkDropOk(state) && (
                <div className="text-danger mt-2">{checkDropOk(state)}</div>
              )}
            </div>
            {files}
          </section>
        )}
      </Dropzone>
    );
  }
}

@observer
export class ImgDropzone extends React.Component<DropzoneProps> {
  @observable acceptedFiles: Array<File> = [];
  @observable content: Array<FileContent> = [];
  @action onDrop = async (files: Array<File>) => {
    this.props.debug && console.log('files:', files);
    this.acceptedFiles = files;
    this.props.debug && console.log('this.files:', this.acceptedFiles);
    this.props.files && this.props.files(this.files);
    await this.loadImages();
  };

  @computed get files() {
    this.props.debug && console.log('get files:', this.acceptedFiles);
    return this.acceptedFiles;
  }

  /// Written thus way because there has to be a Promise chain all the way from
  /// reading the file -> firing onload(end) -> adding to contents -> passing contents
  /// to props.contents. using 'imgFileHandler' below in a call to acceptedFiles.map
  /// breaks the Promise chain resulting in MobX 'out of range error' because props.contents
  /// can be called before they're loaded.
  loadImages = async () => {
    await Promise.all(
      this.files.map(file => {
        /// This is not used...
        const _unused = new Promise(resolve => {
          // try {
          /// --- [ Using dataUri not ArrayBuffer ] ----------------------------------------
          /// Avoid a separate call to readAsArrayBuffer (with all the mucking about with Blob/File
          /// arrays). Just strip off the preamble and save the remaining contents. This is then
          /// a Base64 string which is suitable to upload to the server. The server can decode the
          /// base64.
          /// ------------------------------------------------------------------------------
          // resolve(ImgDropzone.readFileAsUri(file));
          resolve(FileUtility.readFileAsUri(file));
          // } catch (e) {
          //   reject(e);
          // }
        })
          .then(img => {
            // // tslint:disable-next-line: no-shadowed-variable
            const imgUri = img as string;
            /// readAsDataURL returns the entire file Base64-encoded with the following prefix:
            /// data:*/*;base64, (RE /^data:.+/.+;base64,/)

            // let contentType = '';
            // const contentTypeStrings = imgUri.match(/^data:([^;]+);/);
            // if (contentTypeStrings !== null) { contentType = contentTypeStrings[1]; }
            const content = FileUtility.removePreambleFromDataUri(imgUri);
            // const content = imgUri.replace(/^[^,]+,/,'');

            runInAction(() =>
              this.content.push({
                name: file.name,
                dataUri: imgUri,
                // tslint:disable-next-line: no-unnecessary-type-assertion
                content: content as string,
                contentType: FileUtility.extractContentTypeFromDataUri(imgUri)
              })
            );

            return imgUri;
          })
          .catch(reject);
        return _unused;
      })
    )
      /// retval from Promise.all not used, but is a string[]
      .then(_result => _result && this.props.contents && this.props.contents(this.content));
  };

  // /// ===== [ Use Base64 data URI; send base64 to server; decode on server ] =====
  // static readFileAsUri = (file: File): Promise<string> => {
  //   const reader = new FileReader();

  //   ///
  //   /// Do whatever you want with the file contents
  //   ///
  //   return new Promise((resolve, reject) => {
  //     reader.onabort = () => {
  //       // console.log("file reading was aborted");
  //       reject(new DOMException("file->dataUri reading was aborted"));
  //     };
  //     reader.onerror = () => {
  //       reader.abort();
  //       // console.log("file reading has failed");
  //       reject(reader.error);
  //     };
  //     ///
  //     /// --- [ Possible TODO Move some/all of this to reader.onloadend ] ---
  //     ///
  //     reader.onload = () => {
  //       resolve(reader.result as string);
  //     };
  //     reader.readAsDataURL(file);
  //   });
  // };

  renderFileNames = () => {
    return (
      <aside>
        <h4>Files</h4>
        <ul>
          {this.acceptedFiles.length > 0 &&
            this.files.map((file: File) => (
              <li
                // className="list-group-item list-group-item-success"
                key={file.name}
              >
                {file.name}
                <br />
              </li>
            ))}
        </ul>
      </aside>
    );
  };

  render() {
    const files = this.props.debug ? this.renderFileNames() : null;
    const checkDropOk = (state: DropzoneState) => {
      if (state.rejectedFiles.length > 0) {
        if (state.rejectedFiles.length > 1) {
          return FILES_TOO_MANY;
        } else {
          if (state.rejectedFiles[0].size > MAX_IMG_SIZE) {
            return `${state.rejectedFiles[0].name}: ${FILE_TOO_LARGE}`;
          }
          /// --- [ TODO Check this works in other browsers ] ---
          if (!state.rejectedFiles[0].type.match(/^image/)) {
            return `${state.rejectedFiles[0].name}: ${FILE_WRONG_TYPE}`;
          } else {
            return `${state.rejectedFiles[0].name}: ${FILE_ERROR}`;
          }
        }
      } else {
        return null;
      }
    };
    /// Believe there is a bug in the implementation of react-dropzone, in that
    /// isDragReject always returns false. So it's effectively useless
    ///
    /// --- [ TODO Pass props through if set in caller env ] ---
    return (
      <Dropzone onDrop={this.onDrop} accept={IMG_ACCEPT} minSize={0} maxSize={MAX_IMG_SIZE} multiple={false}>
        {(state: DropzoneState) => (
          <section className={styles.container}>
            <div {...state.getRootProps({ className: styles.dropzone })}>
              <input {...state.getInputProps()} />
              {!state.isDragActive && !state.isDragReject && 'Click here or drop a file to upload'}
              {/* {state.isDragActive && !state.isDragReject && "Accepting files..."} */}
              {state.isDragActive && 'Accepting files...'}
              {!state.isDragActive && checkDropOk(state) && (
                <div className="text-danger mt-2">{checkDropOk(state)}</div>
              )}
            </div>
            {files}
          </section>
        )}
      </Dropzone>
    );
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/dropzone/dropzone.tsx